import {action, autorun, computed, observable, runInAction} from 'mobx';
import moment from 'moment';
import {getGoogleCalendarId, setGoogleCalendarId} from '../../../../../utils/userUtility';
import {GOOGLE_API_KEY, GOOGLE_CLIENT_ID} from '../../../../../constants/constants';


class CalendarStore {
    @observable calendarEvents = [];
    @observable gapiLoaded = false;
    @observable calendarEventsLoading = false;
    @observable calendarsList = [];
    @observable accountAdded = false;
    @observable selectedCalendar = {
        label: null,
        value: null
    };


    constructor() {
        autorun(async () => {
            if (this.gapiLoaded && this.accountAdded) {
                await this.getCalendarsList();
                await this.setDefaultCalendar();
            }
        });
    }

    @action
    async setDefaultCalendar() {
        if (this.calendarsList.length) {
            const defaultCalendar = this.calendarsList.find((calendar) => {
                return calendar.value === getGoogleCalendarId();
            });
            if (defaultCalendar) {
                this.selectedCalendar = defaultCalendar;
                await this.getCalendarEvents();
            }
        }
    }

    @computed
    get eventsList() {
        return this.calendarEvents.filter(event => event.start && event.end).map((event) => {
            return {
                start: (event.end.date && new Date(event.start.date)) || new Date(event.start.dateTime),
                end: (event.end.date && new Date(event.end.date)) || new Date(event.end.dateTime),
                title: event.summary,
                id: event.id
            };
        });
    }

    initGapi = async () => {
        const script = document.createElement('script');
        script.onload = async () => {
            // Gapi isn't available immediately so we have to wait until it is to use gapi.
            await this.loadClientWhenGapiReady(script);
        };
        script.src = 'https://apis.google.com/js/client.js';

        document.body.appendChild(script);
    };

    @action loadClientWhenGapiReady = async (script) => {
        if (script.getAttribute('gapi_processed')) {
            try {
                await this.loadClient();
                runInAction(() => {
                    // console.log('gapi loaded');
                    this.gapiLoaded = true;
                });
            } catch (error) {
                // console.log('failed to load gapi: ', error);
            }
        } else {
            // console.log('Client is not ready, trying again in 100ms');
            setTimeout(async () => {
                await this.loadClientWhenGapiReady(script);
            }, 100);
        }
    };

    @action
    insertNewEvent = async (start, end, title) => {
        const event = {
            summary: title,
            start: {dateTime: start},
            end: {dateTime: end}
        };
        try {
            await window.gapi.client.calendar.events.insert({
                calendarId: getGoogleCalendarId(),
                resource: event
            });
        } catch (error) {
            throw error;
        }
    };

    @action
    editEvent = async (start, end, title, id) => {
        const event = {
            summary: title,
            start: {dateTime: start},
            end: {dateTime: end}
        };
        try {
            await window.gapi.client.calendar.events.update({
                calendarId: getGoogleCalendarId(),
                resource: event,
                eventId: id
            });
        } catch (error) {
            throw error;
        }
    };

    @action
    deleteEvent = async (id) => {
        try {
            await window.gapi.client.calendar.events.delete({
                calendarId: getGoogleCalendarId(),
                eventId: id
            });
        } catch (error) {
            throw error;
        }
    };

    auth = () => {
        window.gapi.auth2.getAuthInstance().signIn();
    };

    @action signOut = () => {
        window.gapi.auth2.getAuthInstance().signOut();
        this.updateSignInState(false);
        this.selectedCalendar = {
            label: null,
            value: null
        };
    };

    @action updateSignInState = (state) => {
        this.accountAdded = state;
    };

    async loadClient() {
        try {
            await window.gapi.client.init({
                apiKey: GOOGLE_API_KEY,
                clientId: GOOGLE_CLIENT_ID,
                discoveryDocs: ['https://content.googleapis.com/discovery/v1/apis/calendar/v3/rest'],
                scope: 'https://www.googleapis.com/auth/calendar.readonly https://www.googleapis.com/auth/calendar.events'
            });
            // Listen for sign-in state changes.
            window.gapi.auth2.getAuthInstance().isSignedIn.listen(this.updateSignInState);

            // Handle the initial sign-in state.
            this.updateSignInState(window.gapi.auth2.getAuthInstance().isSignedIn.get());
        } catch (err) {
            console.log(err);
        }
    }

    @action getCalendarsList = async () => {
        if (this.gapiLoaded && this.accountAdded) {
            const response = await window.gapi.client.calendar.calendarList.list({});
            runInAction(() => {
                this.calendarsList = response.result.items
                    .filter(item => item.accessRole === 'owner')
                    .map(calendar => ({value: calendar.id, label: calendar.summary}));
            });
        }
    };

    @action setCalendarId = (val, index) => {
        setGoogleCalendarId(val);
        this.selectedCalendar = this.calendarsList[index];
    };

    @action getCalendarEvents = async () => {
        if (this.gapiLoaded && this.accountAdded) {
            try {
                const now = new Date();
                const timeMin = moment(now).subtract(1, 'months').toISOString();
                const timeMax = moment(now).add(1, 'months').toISOString();
                const response = await window.gapi.client.request({
                    path: `https://www.googleapis.com/calendar/v3/calendars/${getGoogleCalendarId()}/events?singleEvents=true&timeMin=${timeMin}&timeMax=${timeMax}`,
                });
                runInAction(() => {
                    this.calendarEvents = response.result.items;
                });
            } catch (error) {
                throw error;
            } finally {
                runInAction(() => {
                    this.calendarEventsLoading = false;
                });
            }
        }
    };
}

const calendarStore = new CalendarStore();
export default calendarStore;
